import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

export default function Characters() {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedCharacter, setSelectedCharacter] = useState<number | null>(null);
  const navigate = useNavigate();

  const categories = [
    { id: 'all', name: 'Todos', icon: 'ri-group-line' },
    { id: 'patriarchs', name: 'Patriarcas', icon: 'ri-user-star-line' },
    { id: 'prophets', name: 'Profetas', icon: 'ri-speak-line' },
    { id: 'kings', name: 'Reis', icon: 'ri-crown-line' },
    { id: 'apostles', name: 'Apóstolos', icon: 'ri-team-line' },
    { id: 'women', name: 'Mulheres', icon: 'ri-user-heart-line' },
    { id: 'judges', name: 'Juízes', icon: 'ri-scales-line' },
    { id: 'priests', name: 'Sacerdotes', icon: 'ri-building-line' }
  ];

  const characters = [
    {
      id: 1,
      name: 'Abraão',
      category: 'patriarchs',
      title: 'O Pai da Fé',
      period: 'c. 2000 a.C.',
      keyVerse: 'Gênesis 12:1-3',
      description: 'Abraão foi chamado por Deus para deixar sua terra e seguir para uma terra prometida. Sua fé e obediência o tornaram o pai de muitas nações.',
      image: 'https://public.readdy.ai/ai/img_res/bedbd98e52ccfb4cd562428b5fd07150.jpg',
      keyEvents: [
        'Chamado de Deus para deixar Ur',
        'Promessa de descendência numerosa',
        'Nascimento de Isaque',
        'Teste de fé no Monte Moriá'
      ],
      lessons: [
        'A importância da fé em Deus',
        'Obediência mesmo sem entender',
        'Paciência para esperar as promessas',
        'Confiança total em Deus'
      ],
      timeline: 'Viveu aproximadamente 175 anos, de 2166 a 1991 a.C.',
      fullStory: 'Abraão nasceu em Ur dos Caldeus e foi chamado por Deus aos 75 anos para deixar sua terra natal. Deus prometeu fazer dele uma grande nação e abençoar todas as famílias da terra através dele. Abraão obedeceu e partiu para Canaã com sua esposa Sara. Mesmo sendo idoso e Sara estéril, Deus prometeu um filho. Aos 100 anos, Abraão teve Isaque. O maior teste de sua fé foi quando Deus pediu para sacrificar Isaque, mas no último momento Deus providenciou um carneiro.'
    },
    {
      id: 2,
      name: 'Moisés',
      category: 'prophets',
      title: 'O Libertador de Israel',
      period: 'c. 1400 a.C.',
      keyVerse: 'Êxodo 3:10',
      description: 'Moisés foi escolhido por Deus para libertar o povo de Israel da escravidão no Egito e receber a Lei no Monte Sinai.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20Moses%20with%20staff%2C%20parting%20red%20sea%2C%20great%20leader%20and%20prophet%2C%20biblical%20character%20illustration%2C%20divine%20calling%2C%20powerful%20moment&width=400&height=300&seq=moses-char&orientation=landscape',
      keyEvents: [
        'Salvamento das águas do Nilo',
        'Chamado na sarça ardente',
        'As dez pragas do Egito',
        'Travessia do Mar Vermelho',
        'Recebimento dos Dez Mandamentos'
      ],
      lessons: [
        'Deus usa pessoas imperfeitas',
        'A importância da humildade',
        'Liderança através do serviço',
        'Obediência à vontade de Deus'
      ],
      timeline: 'Viveu 120 anos, liderou Israel por 40 anos no deserto',
      fullStory: 'Moisés nasceu durante a escravidão israelita no Egito. Foi salvo das águas do Nilo pela filha de Faraó e criado no palácio. Aos 40 anos, fugiu para o deserto após matar um egípcio. Aos 80 anos, Deus o chamou através da sarça ardente para libertar Israel. Realizou grandes milagres, incluindo as dez pragas e a abertura do Mar Vermelho. Recebeu a Lei de Deus no Monte Sinai e liderou Israel por 40 anos no deserto.'
    },
    {
      id: 3,
      name: 'Davi',
      category: 'kings',
      title: 'O Rei Segundo o Coração de Deus',
      period: 'c. 1000 a.C.',
      keyVerse: '1 Samuel 16:7',
      description: 'Davi foi pastor, guerreiro, músico e rei. Apesar de seus erros, foi chamado de homem segundo o coração de Deus.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20King%20David%20with%20harp%2C%20young%20shepherd%20turned%20king%2C%20biblical%20character%20illustration%2C%20royal%20figure%2C%20musical%20and%20spiritual&width=400&height=300&seq=david-char&orientation=landscape',
      keyEvents: [
        'Unção como rei ainda jovem',
        'Vitória sobre Golias',
        'Amizade com Jônatas',
        'Reinado sobre Israel',
        'Construção de Jerusalém como capital'
      ],
      lessons: [
        'Deus olha o coração, não a aparência',
        'Coragem vem da fé em Deus',
        'Importância do arrependimento',
        'Louvor em todas as circunstâncias'
      ],
      timeline: 'Reinou por 40 anos, de 1010 a 970 a.C.',
      fullStory: 'Davi era o mais novo de oito irmãos e cuidava das ovelhas de seu pai. Foi ungido rei por Samuel ainda jovem. Ganhou fama ao derrotar o gigante Golias com uma pedra. Tornou-se músico na corte do rei Saul e amigo de Jônatas. Fugiu de Saul por anos antes de se tornar rei. Conquistou Jerusalém e fez dela a capital. Escreveu muitos salmos e preparou a construção do templo.'
    },
    {
      id: 4,
      name: 'Daniel',
      category: 'prophets',
      title: 'O Profeta Fiel',
      period: 'c. 600 a.C.',
      keyVerse: 'Daniel 6:10',
      description: 'Daniel manteve sua fé em Deus mesmo no exílio babilônico, sendo conhecido por sua sabedoria e interpretação de sonhos.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20Daniel%20praying%2C%20faithful%20prophet%20in%20Babylon%2C%20biblical%20character%20illustration%2C%20man%20of%20prayer%20and%20wisdom%2C%20spiritual%20devotion&width=400&height=300&seq=daniel-char&orientation=landscape',
      keyEvents: [
        'Exílio para a Babilônia',
        'Interpretação dos sonhos de Nabucodonosor',
        'Cova dos leões',
        'Visões proféticas do futuro'
      ],
      lessons: [
        'Fidelidade a Deus em terra estranha',
        'Importância da oração diária',
        'Coragem para manter convicções',
        'Deus protege os fiéis'
      ],
      timeline: 'Serviu em quatro reinados diferentes por mais de 70 anos',
      fullStory: 'Daniel foi levado cativo para a Babilônia ainda jovem. Recusou-se a se contaminar com a comida do rei e foi abençoado com sabedoria. Interpretou sonhos de Nabucodonosor e se tornou governador. Continuou orando a Deus mesmo quando foi proibido, sendo jogado na cova dos leões. Deus enviou um anjo para protegê-lo. Recebeu visões proféticas sobre o futuro dos reinos mundiais.'
    },
    {
      id: 5,
      name: 'Pedro',
      category: 'apostles',
      title: 'O Apóstolo Impetuoso',
      period: '1º século d.C.',
      keyVerse: 'Mateus 16:16',
      description: 'Pedro foi um dos doze apóstolos de Jesus, conhecido por sua personalidade impetuosa e por ser a pedra sobre a qual Jesus edificaria Sua igreja.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20apostle%20Peter%20with%20keys%2C%20fisherman%20turned%20apostle%2C%20biblical%20character%20illustration%2C%20church%20leader%2C%20spiritual%20authority&width=400&height=300&seq=peter-char&orientation=landscape',
      keyEvents: [
        'Chamado para ser pescador de homens',
        'Confissão de Jesus como Filho de Deus',
        'Negação de Jesus',
        'Restauração após a ressurreição',
        'Liderança da igreja primitiva'
      ],
      lessons: [
        'Deus usa pessoas imperfeitas',
        'Importância do arrependimento',
        'Crescimento através dos erros',
        'Liderança através do amor'
      ],
      timeline: 'Seguiu Jesus por 3 anos, liderou a igreja por décadas',
      fullStory: 'Pedro era pescador no Mar da Galileia quando Jesus o chamou. Era impulsivo e falava antes de pensar. Confessou Jesus como o Cristo e recebeu as chaves do reino. Negou Jesus três vezes durante a crucificação, mas foi restaurado após a ressurreição. Tornou-se o líder da igreja primitiva, pregou no Pentecostes e foi o primeiro a levar o evangelho aos gentios.'
    },
    {
      id: 6,
      name: 'Ester',
      category: 'women',
      title: 'A Rainha Corajosa',
      period: 'c. 500 a.C.',
      keyVerse: 'Ester 4:14',
      description: 'Ester foi uma jovem judia que se tornou rainha da Pérsia e salvou seu povo da destruição através de sua coragem e fé.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20Queen%20Esther%20in%20royal%20attire%2C%20brave%20Jewish%20queen%2C%20biblical%20character%20illustration%2C%20courageous%20woman%2C%20royal%20palace%20setting&width=400&height=300&seq=esther-char&orientation=landscape',
      keyEvents: [
        'Escolhida como rainha da Pérsia',
        'Descoberta do plano de Hamã',
        'Jejum e oração do povo',
        'Corajosa intercessão pelo povo',
        'Salvação dos judeus'
      ],
      lessons: [
        'Deus nos coloca em posições estratégicas',
        'Coragem para defender os oprimidos',
        'Importância da oração e jejum',
        'Usar influência para o bem'
      ],
      timeline: 'Reinou como rainha durante o império Persa',
      fullStory: 'Ester era órfã e foi criada por seu primo Mordecai. Foi escolhida para ser rainha do rei Assuero da Pérsia. Quando Hamã planejou destruir todos os judeus, Mordecai pediu sua ajuda. Ester arriscou sua vida ao se aproximar do rei sem ser chamada. Jejuou por três dias e depois revelou sua identidade judaica ao rei, salvando seu povo da destruição.'
    },
    {
      id: 7,
      name: 'José',
      category: 'patriarchs',
      title: 'O Sonhador',
      period: 'c. 1700 a.C.',
      keyVerse: 'Gênesis 50:20',
      description: 'José foi vendido como escravo pelos irmãos, mas Deus o usou para salvar o Egito e sua família da fome.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20Joseph%20with%20colorful%20coat%2C%20dreamer%20and%20ruler%20in%20Egypt%2C%20biblical%20character%20illustration%2C%20divine%20providence%2C%20ancient%20Egyptian%20setting&width=400&height=300&seq=joseph-char&orientation=landscape',
      keyEvents: [
        'Sonhos proféticos na juventude',
        'Vendido como escravo pelos irmãos',
        'Prisão injusta no Egito',
        'Interpretação dos sonhos de Faraó',
        'Governador do Egito'
      ],
      lessons: [
        'Deus transforma o mal em bem',
        'Fidelidade em tempos difíceis',
        'Perdão aos que nos machucaram',
        'Deus tem um plano maior'
      ],
      timeline: 'Viveu 110 anos, governou o Egito por décadas',
      fullStory: 'José era o filho favorito de Jacó e recebeu uma túnica colorida. Teve sonhos que indicavam que seria superior aos irmãos. Por ciúme, os irmãos o venderam como escravo. No Egito, foi falsamente acusado e preso. Interpretou sonhos na prisão e depois os sonhos de Faraó sobre sete anos de fartura e sete de fome. Tornou-se governador e salvou o Egito e sua família.'
    },
    {
      id: 8,
      name: 'Rute',
      category: 'women',
      title: 'A Nora Fiel',
      period: 'c. 1100 a.C.',
      keyVerse: 'Rute 1:16',
      description: 'Rute foi uma moabita que escolheu seguir sua sogra Noemi e o Deus de Israel, tornando-se ancestral de Davi.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20Ruth%20gleaning%20in%20fields%2C%20loyal%20daughter-in-law%2C%20biblical%20character%20illustration%2C%20faithful%20woman%2C%20ancient%20harvest%20scene&width=400&height=300&seq=ruth-char&orientation=landscape',
      keyEvents: [
        'Casamento com filho de Noemi',
        'Morte do marido',
        'Decisão de seguir Noemi',
        'Trabalho nos campos de Boaz',
        'Casamento com Boaz'
      ],
      lessons: [
        'Lealdade e fidelidade',
        'Deus recompensa a bondade',
        'Importância da família',
        'Deus inclui todos os povos'
      ],
      timeline: 'Viveu durante o período dos juízes',
      fullStory: 'Rute era moabita e casou-se com um israelita. Quando seu marido morreu, sua sogra Noemi decidiu voltar para Israel. Rute escolheu acompanhá-la, dizendo "Teu povo será meu povo, teu Deus será meu Deus". Em Belém, trabalhou nos campos de Boaz, um parente de Noemi. Boaz se casou com Rute, e ela se tornou bisavó do rei Davi.'
    },
    {
      id: 9,
      name: 'Salomão',
      category: 'kings',
      title: 'O Rei Sábio',
      period: 'c. 970 a.C.',
      keyVerse: '1 Reis 3:9',
      description: 'Salomão foi o rei mais sábio de Israel, construiu o templo e escreveu muitos provérbios.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20King%20Solomon%20in%20wisdom%2C%20magnificent%20temple%20builder%2C%20biblical%20character%20illustration%2C%20wise%20ruler%2C%20ancient%20Jerusalem&width=400&height=300&seq=solomon-char&orientation=landscape',
      keyEvents: [
        'Pedido de sabedoria a Deus',
        'Julgamento das duas mães',
        'Construção do templo',
        'Visita da rainha de Sabá',
        'Escrita de provérbios'
      ],
      lessons: [
        'Sabedoria é mais valiosa que riquezas',
        'Importância de buscar a Deus',
        'Responsabilidade da liderança',
        'Cuidado com os excessos'
      ],
      timeline: 'Reinou por 40 anos, de 970 a 930 a.C.',
      fullStory: 'Salomão era filho de Davi e Bate-Seba. Quando se tornou rei, Deus lhe ofereceu qualquer coisa e ele pediu sabedoria para governar. Deus ficou satisfeito e lhe deu sabedoria, riquezas e honra. Construiu o magnífico templo de Jerusalém. Sua sabedoria era famosa no mundo todo. Escreveu milhares de provérbios e cânticos. Infelizmente, no final da vida se desviou de Deus.'
    },
    {
      id: 10,
      name: 'Elias',
      category: 'prophets',
      title: 'O Profeta do Fogo',
      period: 'c. 850 a.C.',
      keyVerse: '1 Reis 18:39',
      description: 'Elias foi um poderoso profeta que confrontou a idolatria e realizou grandes milagres.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20prophet%20Elijah%20calling%20fire%20from%20heaven%2C%20powerful%20prophet%2C%20biblical%20character%20illustration%2C%20divine%20miracle%2C%20Mount%20Carmel%20scene&width=400&height=300&seq=elijah-char&orientation=landscape',
      keyEvents: [
        'Confronto com os profetas de Baal',
        'Fogo do céu no Monte Carmelo',
        'Ressurreição do filho da viúva',
        'Fuga para o deserto',
        'Arrebatamento ao céu'
      ],
      lessons: [
        'Coragem para enfrentar o mal',
        'Deus é o único Deus verdadeiro',
        'Importância da oração',
        'Deus cuida de seus servos'
      ],
      timeline: 'Profetizou durante o reinado de Acabe',
      fullStory: 'Elias surgiu durante o reinado do ímpio rei Acabe e sua esposa Jezabel. Profetizou uma seca de três anos. Foi sustentado por corvos e depois por uma viúva. No Monte Carmelo, desafiou 450 profetas de Baal. Deus enviou fogo do céu para consumir seu sacrifício. Elias matou os falsos profetas e a chuva voltou. Foi arrebatado ao céu numa carruagem de fogo.'
    },
    {
      id: 11,
      name: 'Débora',
      category: 'judges',
      title: 'A Juíza Profetisa',
      period: 'c. 1200 a.C.',
      keyVerse: 'Juízes 4:4',
      description: 'Débora foi a única mulher juíza de Israel, uma profetisa corajosa que liderou o povo à vitória.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20judge%20Deborah%20under%20palm%20tree%2C%20wise%20female%20leader%2C%20biblical%20character%20illustration%2C%20prophetess%20and%20judge%2C%20ancient%20Israel&width=400&height=300&seq=deborah-char&orientation=landscape',
      keyEvents: [
        'Julgamento sob a palmeira',
        'Chamado de Baraque para a guerra',
        'Vitória sobre Sísera',
        'Cântico de vitória',
        'Paz por 40 anos'
      ],
      lessons: [
        'Deus usa mulheres na liderança',
        'Coragem vem de Deus',
        'Importância da obediência',
        'Celebrar as vitórias de Deus'
      ],
      timeline: 'Julgou Israel por 40 anos',
      fullStory: 'Débora era profetisa e juíza em Israel. Sentava-se sob uma palmeira entre Ramá e Betel para julgar o povo. Quando Israel foi oprimido por Jabim, rei de Canaã, Deus lhe disse para chamar Baraque para liderar o exército. Baraque só aceitou se Débora fosse com ele. Deus deu a vitória e Sísera, general inimigo, foi morto por Jael. Débora compôs um cântico de vitória.'
    },
    {
      id: 12,
      name: 'Gideão',
      category: 'judges',
      title: 'O Guerreiro Relutante',
      period: 'c. 1150 a.C.',
      keyVerse: 'Juízes 6:12',
      description: 'Gideão foi chamado por Deus para libertar Israel dos midianitas, vencendo com apenas 300 homens.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20Gideon%20with%20300%20warriors%2C%20brave%20judge%20with%20torches%20and%20trumpets%2C%20biblical%20character%20illustration%2C%20divine%20victory%2C%20ancient%20battle&width=400&height=300&seq=gideon-char&orientation=landscape',
      keyEvents: [
        'Chamado do anjo do Senhor',
        'Destruição do altar de Baal',
        'Sinal do velo de lã',
        'Redução do exército para 300',
        'Vitória sobre os midianitas'
      ],
      lessons: [
        'Deus escolhe os humildes',
        'A vitória vem de Deus, não dos números',
        'Importância de buscar confirmação',
        'Coragem para destruir ídolos'
      ],
      timeline: 'Julgou Israel por 40 anos',
      fullStory: 'Gideão estava malhando trigo escondido quando o anjo do Senhor o chamou de "valente guerreiro". Israel estava oprimido pelos midianitas há sete anos. Gideão duvidou do chamado e pediu sinais. Destruiu o altar de Baal de seu pai. Deus reduziu seu exército de 32.000 para 300 homens. Com tochas, cântaros e trombetas, derrotaram o grande exército midianita.'
    },
    {
      id: 13,
      name: 'Sansão',
      category: 'judges',
      title: 'O Homem Forte',
      period: 'c. 1100 a.C.',
      keyVerse: 'Juízes 16:28',
      description: 'Sansão foi um nazireu com força sobrenatural, mas foi derrotado por sua paixão por Dalila.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20Samson%20with%20long%20hair%20showing%20great%20strength%2C%20powerful%20judge%2C%20biblical%20character%20illustration%2C%20strongman%20of%20Israel%2C%20ancient%20temple&width=400&height=300&seq=samson-char&orientation=landscape',
      keyEvents: [
        'Nascimento milagroso',
        'Matou um leão com as mãos',
        'Matou mil filisteus com queixada',
        'Traição de Dalila',
        'Morte no templo de Dagom'
      ],
      lessons: [
        'Deus dá dons especiais',
        'Cuidado com as tentações',
        'Consequências da desobediência',
        'Deus pode usar até nossos fracassos'
      ],
      timeline: 'Julgou Israel por 20 anos',
      fullStory: 'Sansão nasceu de pais estéreis após promessa de um anjo. Era nazireu desde o nascimento e não podia cortar o cabelo. Tinha força sobrenatural e aterrorizava os filisteus. Apaixonou-se por Dalila, que descobriu o segredo de sua força. Cortaram seu cabelo e ele perdeu a força. Foi cegado e preso. No final, Deus restaurou sua força e ele morreu matando mais filisteus na morte que em vida.'
    },
    {
      id: 14,
      name: 'Samuel',
      category: 'prophets',
      title: 'O Último Juiz',
      period: 'c. 1100 a.C.',
      keyVerse: '1 Samuel 3:10',
      description: 'Samuel foi profeta, sacerdote e juiz, ungiu os primeiros reis de Israel.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20prophet%20Samuel%20anointing%20king%2C%20young%20prophet%20in%20temple%2C%20biblical%20character%20illustration%2C%20priest%20and%20judge%2C%20ancient%20Israel&width=400&height=300&seq=samuel-char&orientation=landscape',
      keyEvents: [
        'Nascimento em resposta à oração',
        'Chamado de Deus ainda criança',
        'Julgamento de Israel',
        'Unção de Saul como rei',
        'Unção de Davi como rei'
      ],
      lessons: [
        'Deus ouve as orações das mães',
        'Importância de ouvir a voz de Deus',
        'Liderança espiritual',
        'Obediência é melhor que sacrifício'
      ],
      timeline: 'Serviu como juiz e profeta por décadas',
      fullStory: 'Samuel nasceu em resposta às orações de sua mãe Ana, que era estéril. Foi dedicado ao Senhor desde pequeno e criado no templo por Eli. Ainda criança, Deus o chamou durante a noite. Tornou-se profeta, sacerdote e o último juiz de Israel. Ungiu Saul como primeiro rei, mas depois o rejeitou por desobediência. Ungiu Davi como o próximo rei conforme a direção de Deus.'
    },
    {
      id: 15,
      name: 'Isaías',
      category: 'prophets',
      title: 'O Profeta Messiânico',
      period: 'c. 700 a.C.',
      keyVerse: 'Isaías 6:8',
      description: 'Isaías foi um grande profeta que profetizou sobre o Messias e o futuro de Israel.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20prophet%20Isaiah%20with%20scroll%2C%20messianic%20prophet%2C%20biblical%20character%20illustration%2C%20temple%20vision%2C%20ancient%20Jerusalem&width=400&height=300&seq=isaiah-char&orientation=landscape',
      keyEvents: [
        'Visão do Senhor no templo',
        'Purificação com brasa do altar',
        'Profecia do Emanuel',
        'Profecia do Servo Sofredor',
        'Ministério por 60 anos'
      ],
      lessons: [
        'Deus é santo e glorioso',
        'Importância da purificação',
        'Disponibilidade para servir',
        'Esperança no Messias'
      ],
      timeline: 'Profetizou durante 4 reinados diferentes',
      fullStory: 'Isaías teve uma visão gloriosa de Deus no templo, com serafins clamando "Santo, Santo, Santo". Sentiu-se impuro, mas foi purificado com uma brasa do altar. Quando Deus perguntou "A quem enviarei?", ele respondeu "Eis-me aqui, envia-me a mim". Profetizou por 60 anos durante reinados de Uzias, Jotão, Acaz e Ezequias. Suas profecias sobre o Messias são as mais detalhadas do Antigo Testamento.'
    },
    {
      id: 16,
      name: 'Jeremias',
      category: 'prophets',
      title: 'O Profeta Chorão',
      period: 'c. 600 a.C.',
      keyVerse: 'Jeremias 1:5',
      description: 'Jeremias foi chamado desde o ventre materno para ser profeta das nações, profetizou a queda de Jerusalém.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20prophet%20Jeremiah%20weeping%2C%20sorrowful%20prophet%2C%20biblical%20character%20illustration%2C%20prophet%20of%20judgment%2C%20ancient%20Jerusalem%20destruction&width=400&height=300&seq=jeremiah-char&orientation=landscape',
      keyEvents: [
        'Chamado desde o ventre',
        'Visão da amendoeira',
        'Profecia da queda de Jerusalém',
        'Prisão e perseguição',
        'Exílio para o Egito'
      ],
      lessons: [
        'Deus nos conhece antes do nascimento',
        'Coragem para falar a verdade',
        'Perseverança na adversidade',
        'Deus disciplina por amor'
      ],
      timeline: 'Profetizou por mais de 40 anos',
      fullStory: 'Jeremias foi chamado por Deus ainda jovem para ser profeta das nações. Relutou por se achar muito jovem, mas Deus o encorajou. Profetizou a queda de Jerusalém e o exílio babilônico. Foi perseguido, preso e jogado numa cisterna. Chorava pelo pecado do povo e a destruição que viria. Presenciou a queda de Jerusalém e foi levado para o Egito contra sua vontade.'
    },
    {
      id: 17,
      name: 'Ezequiel',
      category: 'prophets',
      title: 'O Profeta Visionário',
      period: 'c. 590 a.C.',
      keyVerse: 'Ezequiel 36:26',
      description: 'Ezequiel foi um profeta sacerdote que teve visões extraordinárias durante o exílio babilônico.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20prophet%20Ezekiel%20with%20divine%20visions%2C%20mystical%20prophet%2C%20biblical%20character%20illustration%2C%20heavenly%20wheels%2C%20exile%20in%20Babylon&width=400&height=300&seq=ezekiel-char&orientation=landscape',
      keyEvents: [
        'Visão dos seres viventes',
        'Chamado para ser atalaia',
        'Visão do vale de ossos secos',
        'Visão do novo templo',
        'Profecias sobre as nações'
      ],
      lessons: [
        'Deus é glorioso e misterioso',
        'Responsabilidade de advertir',
        'Deus pode ressuscitar o morto',
        'Esperança de restauração'
      ],
      timeline: 'Profetizou durante 22 anos no exílio',
      fullStory: 'Ezequiel era sacerdote e foi levado cativo para a Babilônia. Aos 30 anos, teve uma visão extraordinária de Deus com seres viventes e rodas cheias de olhos. Foi chamado para ser atalaia do povo. Usou muitas dramatizações para ilustrar suas profecias. Teve a famosa visão do vale de ossos secos, representando a restauração de Israel. Também viu o novo templo que seria construído.'
    },
    {
      id: 18,
      name: 'João Batista',
      category: 'prophets',
      title: 'O Precursor',
      period: '1º século d.C.',
      keyVerse: 'João 1:23',
      description: 'João Batista foi o precursor de Jesus, preparando o caminho do Senhor através do batismo e pregação.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20john%20the%20Baptist%20baptizing%20in%20river%2C%20wilderness%20preacher%2C%20biblical%20character%20illustration%2C%20forerunner%20of%20Christ%2C%20Jordan%20river&width=400&height=300&seq=john-baptist-char&orientation=landscape',
      keyEvents: [
        'Nascimento milagroso',
        'Ministério no deserto',
        'Batismo de Jesus',
        'Testemunho sobre Jesus',
        'Prisão e martírio'
      ],
      lessons: [
        'Importância de preparar o caminho',
        'Humildade para diminuir',
        'Coragem para confrontar o pecado',
        'Fidelidade até a morte'
      ],
      timeline: 'Ministrou por cerca de 2 anos',
      fullStory: 'João nasceu de pais idosos, Zacarias e Isabel. Seu nascimento foi anunciado por um anjo. Cresceu no deserto e começou a pregar arrependimento. Batizava no rio Jordão e muitos vinham a ele. Quando Jesus veio para ser batizado, João o reconheceu como o Cordeiro de Deus. Continuou testemunhando sobre Jesus. Foi preso por Herodes por confrontar seu adultério e foi decapitado.'
    },
    {
      id: 19,
      name: 'Paulo',
      category: 'apostles',
      title: 'O Apóstolo dos Gentios',
      period: '1º século d.C.',
      keyVerse: 'Atos 9:15',
      description: 'Paulo foi o grande missionário que levou o evangelho aos gentios e escreveu grande parte do Novo Testamento.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20apostle%20Paul%20writing%20letters%2C%20missionary%20to%20gentiles%2C%20biblical%20character%20illustration%2C%20great%20evangelist%2C%20ancient%20scrolls&width=400&height=300&seq=paul-char&orientation=landscape',
      keyEvents: [
        'Perseguição aos cristãos',
        'Conversão no caminho de Damasco',
        'Três viagens missionárias',
        'Prisão em Roma',
        'Escrita de 13 cartas'
      ],
      lessons: [
        'Deus pode transformar inimigos',
        'Importância da evangelização',
        'Perseverança nas dificuldades',
        'Graça de Deus é suficiente'
      ],
      timeline: 'Ministrou por cerca de 30 anos',
      fullStory: 'Paulo, antes chamado Saulo, era fariseu e perseguia os cristãos. No caminho para Damasco, Jesus lhe apareceu numa luz brilhante e ele se converteu. Tornou-se o maior missionário da história, fazendo três viagens missionárias. Plantou igrejas por todo o império romano. Foi preso várias vezes e finalmente levado a Roma. Escreveu 13 cartas que formam grande parte do Novo Testamento.'
    },
    {
      id: 20,
      name: 'Maria',
      category: 'women',
      title: 'A Mãe de Jesus',
      period: '1º século d.C.',
      keyVerse: 'Lucas 1:38',
      description: 'Maria foi escolhida por Deus para ser a mãe de Jesus, demonstrando fé e obediência extraordinárias.',
      image: 'https://readdy.ai/api/search-image?query=Biblical%20Mary%20mother%20of%20Jesus%2C%20virgin%20mother%2C%20biblical%20character%20illustration%2C%20blessed%20among%20women%2C%20nativity%20scene&width=400&height=300&seq=mary-char&orientation=landscape',
      keyEvents: [
        'Anunciação do anjo Gabriel',
        'Visitação a Isabel',
        'Nascimento de Jesus',
        'Apresentação no templo',
        'Aos pés da cruz'
      ],
      lessons: [
        'Disponibilidade para servir a Deus',
        'Fé nas promessas divinas',
        'Humildade diante do chamado',
        'Perseverança no sofrimento'
      ],
      timeline: 'Viveu durante o ministério de Jesus',
      fullStory: 'Maria era uma jovem virgem de Nazaré, noiva de José. O anjo Gabriel lhe anunciou que seria mãe do Messias pelo poder do Espírito Santo. Apesar da situação difícil, ela aceitou dizendo "Faça-se em mim segundo a tua palavra". Visitou sua prima Isabel e cantou o Magnificat. Deu à luz Jesus em Belém. Acompanhou Jesus durante seu ministério e esteve aos pés da cruz.'
    }
  ];

  const filteredCharacters = selectedCategory === 'all' 
    ? characters 
    : characters.filter(char => char.category === selectedCategory);

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/login');
  };

  const selectedCharacterData = characters.find(char => char.id === selectedCharacter);

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
      {/* Header */}
      <header className="bg-white/95 backdrop-blur-sm shadow-xl border-b-4 border-gradient-to-r from-indigo-600 to-pink-600">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://static.readdy.ai/image/8fbfc975813ef59021c9f16b313e86f9/f5810bc36c96b4c4a03e9a9aa341e507.png"
                alt="Assembleia de Deus - Missão Europa"
                className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 object-contain shadow-2xl rounded-2xl border-4 border-indigo-200"
              />
              <div className="text-center flex-1">
                <h1 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-bold bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 bg-clip-text text-transparent whitespace-nowrap">
                  <span style={{ fontFamily: '"Agnes", serif' }}>IGREJA AD MISSÃO EUROPA CHURCH</span>
                </h1>
                <p className="text-sm text-gray-600">Church Portugal</p>
              </div>
            </div>
            
            {/* Navigation */}
            <nav className="flex items-center space-x-6">
              <button 
                onClick={() => navigate('/')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-home-line"></i>
                <span>Início</span>
              </button>
              <button 
                onClick={() => navigate('/bible')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-book-line"></i>
                <span>Bíblia</span>
              </button>
              <button 
                onClick={() => navigate('/characters')}
                className="flex items-center space-x-2 px-4 py-2 text-indigo-600 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors whitespace-nowrap"
              >
                <i className="ri-user-star-line"></i>
                <span>Personagens</span>
              </button>
              <button 
                onClick={() => navigate('/radio')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-radio-line"></i>
                <span>Rádio</span>
              </button>
              <button 
                onClick={() => navigate('/kids')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-bear-smile-line"></i>
                <span>Crianças</span>
              </button>
              <button 
                onClick={() => navigate('/devotionals')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-heart-line"></i>
                <span>Devocionais</span>
              </button>
              <button 
                onClick={() => navigate('/studies')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-graduation-cap-line"></i>
                <span>Estudos</span>
              </button>
              <button 
                onClick={() => navigate('/prayer-requests')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-hands-line"></i>
                <span>Oração</span>
              </button>
              <button 
                onClick={() => navigate('/location')}
                className="flex items-center space-x-2 px-4 py-2 text-gray-600 hover:text-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-map-pin-line"></i>
                <span>Localização</span>
              </button>
              <button 
                onClick={handleLogout}
                className="flex items-center space-x-2 px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors whitespace-nowrap"
              >
                <i className="ri-logout-box-line"></i>
                <span>Sair</span>
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Banner */}
      <div className="relative h-64 bg-gradient-to-r from-indigo-600 via-purple-600 to-pink-600 overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <img 
          src="https://readdy.ai/api/search-image?query=Biblical%20characters%20collage%2C%20ancient%20heroes%20of%20faith%2C%20diverse%20biblical%20figures%2C%20spiritual%20leaders%20throughout%20history%2C%20inspirational%20religious%20art&width=1200&height=300&seq=characters-banner&orientation=landscape"
          alt="Personagens Bíblicos"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center text-white">
            <h2 className="text-4xl font-bold mb-2">Personagens Bíblicos</h2>
            <p className="text-xl opacity-90">Conheça os heróis da fé</p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {!selectedCharacter ? (
          <>
            {/* Categorias */}
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-white mb-6">Categorias</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4">
                {categories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSelectedCategory(category.id)}
                    className={`p-4 rounded-xl transition-all duration-200 whitespace-nowrap ${
                      selectedCategory === category.id
                        ? 'bg-white text-indigo-600 shadow-xl transform scale-105'
                        : 'bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm'
                    }`}
                  >
                    <div className="flex flex-col items-center space-y-2">
                      <i className={`${category.icon} text-2xl`}></i>
                      <span className="text-sm font-medium">{category.name}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* Lista de Personagens */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
              {filteredCharacters.map((character) => (
                <div key={character.id} className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden hover:shadow-3xl transition-all duration-300 transform hover:scale-105 border border-white/20">
                  <div className="relative h-48">
                    <img 
                      src={character.image}
                      alt={character.name}
                      className="w-full h-full object-cover object-top"
                    />
                    <div className="absolute top-4 right-4">
                      <span className="bg-white/90 backdrop-blur-sm text-indigo-600 px-3 py-1 rounded-full text-xs font-medium">
                        {character.period}
                      </span>
                    </div>
                  </div>
                  
                  <div className="p-6">
                    <h3 className="text-xl font-bold bg-gradient-to-r from-indigo-600 to-pink-600 bg-clip-text text-transparent mb-1">
                      {character.name}
                    </h3>
                    <p className="text-purple-600 font-medium mb-3">{character.title}</p>
                    <p className="text-gray-600 mb-4 line-clamp-3">{character.description}</p>
                    
                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <i className="ri-book-open-line mr-2 text-indigo-500"></i>
                        <span>{character.keyVerse}</span>
                      </div>
                    </div>
                    
                    <button
                      onClick={() => setSelectedCharacter(character.id)}
                      className="w-full bg-gradient-to-r from-indigo-600 to-pink-600 text-white py-3 rounded-lg font-medium hover:from-indigo-700 hover:to-pink-700 transition-all duration-200 transform hover:scale-105"
                    >
                      Ver mais
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        ) : (
          /* Detalhes do Personagem */
          <div className="max-w-5xl mx-auto">
            <button
              onClick={() => setSelectedCharacter(null)}
              className="flex items-center space-x-2 text-white hover:text-indigo-200 mb-6 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              <span>Ver menos</span>
            </button>
            
            {selectedCharacterData && (
              <div className="bg-white/95 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden border border-white/20">
                <div className="relative h-64 md:h-80">
                  <img 
                    src={selectedCharacterData.image}
                    alt={selectedCharacterData.name}
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                  <div className="absolute bottom-6 left-6 text-white">
                    <h2 className="text-3xl md:text-4xl font-bold">{selectedCharacterData.name}</h2>
                    <p className="text-xl opacity-90">{selectedCharacterData.title}</p>
                  </div>
                </div>
                
                <div className="p-6 md:p-8">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                    <div className="md:col-span-2">
                      <h3 className="text-xl font-bold text-indigo-600 mb-4">História Completa</h3>
                      <p className="text-gray-700 leading-relaxed">{selectedCharacterData.fullStory}</p>
                    </div>
                    
                    <div className="space-y-6">
                      <div>
                        <h4 className="font-bold text-purple-600 mb-2">Período</h4>
                        <p className="text-gray-700">{selectedCharacterData.period}</p>
                      </div>
                      
                      <div>
                        <h4 className="font-bold text-purple-600 mb-2">Linha do Tempo</h4>
                        <p className="text-gray-700">{selectedCharacterData.timeline}</p>
                      </div>
                      
                      <div>
                        <h4 className="font-bold text-purple-600 mb-2">Versículo Chave</h4>
                        <p className="text-indigo-600 font-medium">{selectedCharacterData.keyVerse}</p>
                      </div>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div>
                      <h3 className="text-xl font-bold text-indigo-600 mb-4">Eventos Principais</h3>
                      <ul className="space-y-2">
                        {selectedCharacterData.keyEvents.map((event, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <i className="ri-check-line text-green-500 mt-1"></i>
                            <span className="text-gray-700">{event}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h3 className="text-xl font-bold text-indigo-600 mb-4">Lições Importantes</h3>
                      <ul className="space-y-2">
                        {selectedCharacterData.lessons.map((lesson, index) => (
                          <li key={index} className="flex items-start space-x-3">
                            <i className="ri-lightbulb-line text-yellow-500 mt-1"></i>
                            <span className="text-gray-700">{lesson}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
